import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  InputAdornment
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { api } from '../../utils/api';
import { openMaintenanceReviewWhatsApp } from '../../utils/whatsappTemplates';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import MaintenanceReceiptPrint from './MaintenanceReceiptPrint';
import logger from '../../utils/logger';

const CompleteMaintenanceDialog = ({ open, onClose, maintenance, onCompleted }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printData, setPrintData] = useState(null);

  // Calcola il saldo da versare - Avvolto in useCallback per evitare re-rendering
  const calculateBalance = useCallback(() => {
    // Utilizziamo direttamente il valore finalCost dal database
    const total = parseFloat(maintenance?.finalCost || 0);
    const discount = parseFloat(maintenance?.discount || 0);
    const deposit = parseFloat(maintenance?.deposit || 0);
    
    // Calcola il totale con sconto e sottrae l'acconto
    const totalWithDiscount = total - discount;
    const balance = totalWithDiscount - deposit;
    
    // 🔒 SICUREZZA: Log solo in development
    logger.debug('DEBUG - valori manutenzione:', {
      finalCost: maintenance?.finalCost,
      discount: maintenance?.discount,
      deposit: maintenance?.deposit,
      totalWithDiscount,
      balance
    });
    
    return balance;
  }, [maintenance]);

  const formatAmount = (amount) => {
    return Number(amount).toFixed(2);
  };

  const { control, handleSubmit, reset, watch, formState: { isValid } } = useForm({
    defaultValues: {
      paymentAmount: calculateBalance(),
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      paymentMethodId: '',
      accountId: ''
    },
    mode: 'onChange'
  });

  // Monitora i valori dei campi
  const watchAllFields = watch();

  // Carica i metodi di pagamento e i conti
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [methodsRes, accountsRes] = await Promise.all([
          api.get('/payment-methods'),
          api.get('/accounts')
        ]);
        setPaymentMethods(methodsRes.data);
        setAccounts(accountsRes.data);
      } catch (err) {
        setError('Errore nel caricamento delle opzioni');
        console.error('Errore nel caricamento delle opzioni:', err);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  // Reset form quando il dialog si apre
  useEffect(() => {
    if (open && maintenance) {
      reset({
        paymentAmount: calculateBalance(),
        paymentDate: format(new Date(), 'yyyy-MM-dd'),
        paymentMethodId: '',
        accountId: ''
      });
      setSelectedPaymentMethod(null);
      setSelectedAccount(null);
    }
  }, [open, maintenance, reset, calculateBalance]);

  // Aggiorna il metodo di pagamento selezionato
  useEffect(() => {
    if (watchAllFields.paymentMethodId && paymentMethods.length > 0) {
      const method = paymentMethods.find(
        m => m.id === parseInt(watchAllFields.paymentMethodId)
      );
      setSelectedPaymentMethod(method);
    } else {
      setSelectedPaymentMethod(null);
    }
  }, [watchAllFields.paymentMethodId, paymentMethods]);

  // Aggiorna il conto selezionato
  useEffect(() => {
    if (watchAllFields.accountId && accounts.length > 0) {
      const account = accounts.find(
        a => a.id === parseInt(watchAllFields.accountId)
      );
      setSelectedAccount(account);
    } else {
      setSelectedAccount(null);
    }
  }, [watchAllFields.accountId, accounts]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Assicurati che l'ID della manutenzione sia disponibile
      if (!maintenance?.id) {
        throw new Error('ID manutenzione non valido');
      }

      // Log dei dati che stiamo per inviare
      console.log('Dati manutenzione:', maintenance);
      console.log('Dati form:', data);

      const payload = {
        paymentAmount: parseFloat(data.paymentAmount),
        paymentDate: data.paymentDate,
        paymentMethodId: parseInt(data.paymentMethodId),
        accountId: parseInt(data.accountId)
      };

      console.log('Payload da inviare:', payload);

      // Effettua la chiamata API con i dati corretti
      const response = await api.put(`/maintenance/${maintenance.id}/complete`, payload);

      console.log('Risposta dal server:', response);

      if (response.data) {
        try {
          const m = response.data.maintenance;
          await openMaintenanceReviewWhatsApp(
            m && typeof m.id !== 'undefined' ? m : { id: maintenance.id }
          );
        } catch (e) {
          logger.error('WhatsApp recensioni dopo completamento manutenzione:', e);
        }
        onCompleted();
        onClose();
      }
    } catch (err) {
      console.error('Errore completo:', err);
      console.error('Dettagli errore:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.msg || 'Errore durante il completamento della manutenzione');
    } finally {
      setLoading(false);
    }
  };

  // Prepara i dati per la stampa della ricevuta
  const handlePrintReceipt = () => {
    // Log dei dati per debugging
    console.log("DEBUG - Dati manutenzione per ricevuta:", {
      id: maintenance?.id,
      customerName: maintenance?.customerName,
      customerPhone: maintenance?.customerPhone,
      bikeBrand: maintenance?.bikeBrand,
      bikeModel: maintenance?.bikeModel,
      protocolNumber: maintenance?.protocolNumber,
      finalCost: maintenance?.finalCost,
      deposit: maintenance?.deposit
    });

    // Aggiungiamo manualmente i campi mancanti con gestione sicura dei null
    const enhancedMaintenance = {
      ...maintenance,
      customerName: maintenance?.customerName || (maintenance?.Customer ? `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}` : 'N/D'),
      customerPhone: maintenance?.customerPhone || maintenance?.Customer?.phone || 'N/D',
      bikeBrand: maintenance?.bikeBrand || maintenance?.Vehicle?.brand || 'N/D',
      bikeModel: maintenance?.bikeModel || maintenance?.Vehicle?.model || 'N/D'
    };
    
    setPrintData({
      maintenance: enhancedMaintenance,
      paymentMethod: selectedPaymentMethod,
      account: selectedAccount,
      paymentAmount: watchAllFields.paymentAmount,
      paymentDate: watchAllFields.paymentDate
    });
    setShowPrintDialog(true);
  };

  // Chiude il dialog di stampa
  const handleClosePrintDialog = () => {
    setShowPrintDialog(false);
  };

  // Stampa diretta della ricevuta tramite window.print()
  const handleDirectPrint = () => {
    const printWindow = window.open('', 'PRINT', 'width=600,height=800');
    const receiptHtml = document.getElementById('receipt-print').outerHTML;
    printWindow.document.write(`
      <html><head><title>Stampa Ricevuta</title>
        <style>
          /* Costringerà il browser a stampare in A5 con margini */
          @page { size: A5; margin: 10mm; }
          body {
            margin: 0;
            padding: 0;
          }
          /* Wrapper della ricevuta: dimensioni reali */
          #receipt-print {
            width: 148mm !important;
            padding: 10mm !important;
          }
          /* Ogni "page" del template: niente altezza fissa, page-break e spazio */
          .receipt-page {
            page-break-after: always !important;
            break-inside: avoid !important;
            height: auto !important;
            margin-bottom: 10mm !important;
          }
          .receipt-page:last-child {
            page-break-after: auto !important;
          }
        </style>
      </head><body>${receiptHtml}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Completa Manutenzione</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Totale intervento */}
              <Grid item xs={12}>
                <TextField
                  label="Totale intervento"
                  value={`€${formatAmount(maintenance?.finalCost || 0)}`}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              
              {/* Sconto */}
              {parseFloat(maintenance?.discount || 0) > 0 && (
                <Grid item xs={12}>
                  <TextField
                    label="Sconto"
                    value={`-€${formatAmount(maintenance?.discount || 0)}`}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'error.main',
                        fontWeight: 'medium'
                      }
                    }}
                  />
                </Grid>
              )}
              
              {/* Acconto versato */}
              <Grid item xs={12}>
                <TextField
                  label="Acconto versato"
                  value={`€${formatAmount(maintenance?.deposit || 0)}`}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              
              {/* Saldo da versare */}
              <Grid item xs={12}>
                <TextField
                  label="Saldo da versare"
                  value={`€${formatAmount(calculateBalance())}`}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              
              {/* Importo Pagamento */}
              <Grid item xs={12}>
                <Controller
                  name="paymentAmount"
                  control={control}
                  rules={{
                    required: 'Importo richiesto',
                    validate: {
                      isValidNumber: (value) => {
                        const num = parseFloat(value);
                        if (isNaN(num)) return 'Inserisci un numero valido';
                        // Permetti importi negativi per rimborsi
                        return true;
                      }
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Importo Pagamento"
                      type="number"
                      fullWidth
                      error={!!error}
                      helperText={
                        error?.message || 
                        (parseFloat(field.value) < 0 ? '⚠️ Importo negativo: verrà registrato come rimborso (movimento in uscita)' : '')
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        inputProps: { step: "0.01" } // Rimuove validazione automatica per negativi
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="paymentDate"
                  control={control}
                  rules={{ required: 'Data richiesta' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Data Pagamento"
                      type="date"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="paymentMethodId"
                  control={control}
                  rules={{ required: 'Metodo di pagamento richiesto' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Metodo di Pagamento"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    >
                      {paymentMethods.map((method) => (
                        <MenuItem key={method.id} value={method.id}>
                          {method.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="accountId"
                  control={control}
                  rules={{ required: 'Conto richiesto' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Conto"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    >
                      {accounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={loading}>
              Annulla
            </Button>
            <Button onClick={handlePrintReceipt} disabled={loading} startIcon={<PrintIcon />}>
              Stampa
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading || !isValid}>
              Completa
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog per la stampa */}
      <Dialog open={showPrintDialog} onClose={handleClosePrintDialog} maxWidth="md" fullWidth>
        <DialogTitle>Anteprima Ricevuta</DialogTitle>
        <DialogContent>
          <div id="receipt-print">
            <MaintenanceReceiptPrint data={printData} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog}>
            Chiudi
          </Button>
          <Button onClick={handleDirectPrint} variant="contained" color="secondary" startIcon={<PrintIcon />}>
            Stampa Ricevuta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per gli errori */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompleteMaintenanceDialog;